import * as React from 'react'
import styled from 'styled-components'
import { Panel } from '@components/panels/panels'
import { PathLink } from '@shared/components/navigation'
import { newMemberPath, organizationPath } from '@logic'
import { Uuid } from '@helpers/util'

interface Props {
  organization: Uuid
}

const EmptyStateContainer = styled.div`
  padding: 32px 24px;
  text-align: center;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${props => props.theme.white};
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary || '#9CA6AD'};
  margin-bottom: 32px;
  line-height: 1.5;
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  background: ${props => props.theme.panelBack || '#1a2332'};
  border: 1px solid ${props => props.theme.border || 'rgba(255,255,255,0.08)'};
  text-align: left;
`

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #33AA40;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`

const StepTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.white};
  margin-bottom: 4px;
`

const StepDescription = styled.div`
  font-size: 13px;
  color: ${props => props.theme.textSecondary || '#9CA6AD'};
  line-height: 1.4;
`

const ActionLink = styled(PathLink)`
  display: inline-block;
  padding: 10px 24px;
  background: #33AA40;
  color: #fff;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #2d9438;
  }
`

export const DashboardEmptyState = ({ organization }: Props) => {
  return (
    <Panel>
      <EmptyStateContainer>
        <Title>Get started with your cap table</Title>
        <Subtitle>
          Set up your organization by adding shareholders and recording equity transactions.
        </Subtitle>
        <StepsGrid>
          <StepCard>
            <StepNumber>1</StepNumber>
            <StepTitle>Add shareholders</StepTitle>
            <StepDescription>
              Add your founders, investors, and team members as shareholders.
            </StepDescription>
          </StepCard>
          <StepCard>
            <StepNumber>2</StepNumber>
            <StepTitle>Issue shares</StepTitle>
            <StepDescription>
              Record common stock, preferred stock, or options for each shareholder.
            </StepDescription>
          </StepCard>
          <StepCard>
            <StepNumber>3</StepNumber>
            <StepTitle>Track ownership</StepTitle>
            <StepDescription>
              View your ownership breakdown, dilution, and cap table history.
            </StepDescription>
          </StepCard>
        </StepsGrid>
        <ActionLink
          path={newMemberPath}
          args={{ organization }}
        >
          Add your first shareholder
        </ActionLink>
      </EmptyStateContainer>
    </Panel>
  )
}
